import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart as RLineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  AlertTriangle,
  FileText,
  Search,
  MousePointerClick,
  Eye,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { listSeoSnapshots, captureSeoSnapshotNow } from "@/lib/seo-monitoring.functions";

export const Route = createFileRoute("/_authenticated/admin/seo")({
  component: AdminSeoPage,
});

function formatDate(iso: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function Stat({
  label,
  value,
  icon: Icon,
  tone = "default",
}: {
  label: string;
  value: React.ReactNode;
  icon: typeof Search;
  tone?: "default" | "good" | "bad" | "warn";
}) {
  const toneClass =
    tone === "good"
      ? "text-emerald-400"
      : tone === "bad"
        ? "text-red-400"
        : tone === "warn"
          ? "text-amber-400"
          : "text-foreground";
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
        <Icon className={`h-3.5 w-3.5 ${toneClass}`} />
      </div>
      <div className={`mt-1.5 text-xl font-bold tabular-nums ${toneClass}`}>{value}</div>
    </Card>
  );
}

function AdminSeoPage() {
  const fetchHistory = useServerFn(listSeoSnapshots);
  const captureNow = useServerFn(captureSeoSnapshotNow);
  const qc = useQueryClient();
  const [days, setDays] = useState(30);

  const history = useQuery({
    queryKey: ["admin", "seo", "snapshots", days],
    queryFn: () => fetchHistory({ data: { days } }),
    refetchInterval: 5 * 60_000,
  });

  const capture = useMutation({
    mutationFn: () => captureNow(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "seo"] }),
  });

  const snapshots = history.data?.snapshots ?? [];
  const latest = snapshots.length > 0 ? snapshots[snapshots.length - 1] : null;

  const chartData = snapshots.map((s) => ({
    date: new Date(s.captured_at).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    }),
    indexed: s.indexed_pages,
    submitted: s.submitted_pages,
    clicks: s.clicks_7d,
    impressions: s.impressions_7d,
    position: s.position_7d,
  }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold tracking-tight">SEO Monitoring</h2>
          <p className="text-xs text-muted-foreground">
            Google Search Console verification, sitemap, indexing, and search-analytics history.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="h-8 rounded-md border border-border/60 bg-card px-2 text-xs"
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
          </select>
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5 text-xs"
            disabled={capture.isPending}
            onClick={() => capture.mutate()}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${capture.isPending ? "animate-spin" : ""}`} />
            {capture.isPending ? "Capturing…" : "Capture snapshot now"}
          </Button>
        </div>
      </div>

      {capture.error && (
        <Card className="border-red-500/40 bg-red-500/5 p-3 text-xs text-red-300">
          {(capture.error as Error).message}
        </Card>
      )}

      {history.isLoading ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : !latest ? (
        <Card className="p-6 text-center text-sm text-muted-foreground">
          No snapshots yet. Click <strong>Capture snapshot now</strong> to record the first one. A
          daily snapshot will then run automatically.
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat
              label="Verification"
              value={latest.verified ? "Verified" : "Failing"}
              icon={latest.verified ? CheckCircle2 : XCircle}
              tone={latest.verified ? "good" : "bad"}
            />
            <Stat
              label="Sitemap status"
              value={
                latest.sitemap_errors > 0
                  ? `${latest.sitemap_errors} err`
                  : latest.sitemap_warnings > 0
                    ? `${latest.sitemap_warnings} warn`
                    : latest.sitemap_is_pending
                      ? "Pending"
                      : "OK"
              }
              icon={
                latest.sitemap_errors > 0
                  ? XCircle
                  : latest.sitemap_warnings > 0
                    ? AlertTriangle
                    : CheckCircle2
              }
              tone={
                latest.sitemap_errors > 0
                  ? "bad"
                  : latest.sitemap_warnings > 0
                    ? "warn"
                    : "good"
              }
            />
            <Stat
              label="Indexed / Submitted"
              value={`${latest.indexed_pages} / ${latest.submitted_pages}`}
              icon={FileText}
            />
            <Stat
              label="Avg position (7d)"
              value={latest.position_7d > 0 ? latest.position_7d.toFixed(1) : "—"}
              icon={Search}
            />
            <Stat
              label="Clicks (7d)"
              value={latest.clicks_7d.toLocaleString()}
              icon={MousePointerClick}
            />
            <Stat
              label="Impressions (7d)"
              value={latest.impressions_7d.toLocaleString()}
              icon={Eye}
            />
            <Stat
              label="CTR (7d)"
              value={`${(latest.ctr_7d * 100).toFixed(2)}%`}
              icon={MousePointerClick}
            />
            <Stat
              label="Last sitemap submit"
              value={
                <span className="text-xs font-medium">
                  {formatDate(latest.sitemap_last_submitted)}
                </span>
              }
              icon={FileText}
            />
          </div>

          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold">Indexing over time</h3>
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Submitted vs indexed
              </span>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <RLineChart data={chartData}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      fontSize: 11,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="submitted"
                    stroke="hsl(220 80% 60%)"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="indexed"
                    stroke="hsl(150 70% 50%)"
                    strokeWidth={2}
                    dot={false}
                  />
                </RLineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            <Card className="p-4">
              <h3 className="mb-3 text-sm font-semibold">Clicks & impressions (7d window)</h3>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <RLineChart data={chartData}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip
                      contentStyle={{
                        background: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        fontSize: 11,
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="clicks"
                      stroke="hsl(45 95% 60%)"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="impressions"
                      stroke="hsl(280 80% 65%)"
                      strokeWidth={2}
                      dot={false}
                    />
                  </RLineChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-4">
              <h3 className="mb-3 text-sm font-semibold">Average position (7d window)</h3>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <RLineChart data={chartData}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} reversed domain={[1, "dataMax + 5"]} />
                    <Tooltip
                      contentStyle={{
                        background: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        fontSize: 11,
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="position"
                      stroke="hsl(195 90% 55%)"
                      strokeWidth={2}
                      dot={false}
                    />
                  </RLineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <Card className="p-4">
            <h3 className="mb-3 text-sm font-semibold">Snapshot history</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="py-1.5 pr-3">Captured</th>
                    <th className="px-3">Verified</th>
                    <th className="px-3">Sitemap</th>
                    <th className="px-3">Indexed</th>
                    <th className="px-3">Clicks</th>
                    <th className="px-3">Impr.</th>
                    <th className="px-3">CTR</th>
                    <th className="px-3">Pos.</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {[...snapshots].reverse().slice(0, 30).map((s) => (
                    <tr key={s.id} className="border-t border-border/40">
                      <td className="py-1.5 pr-3">{formatDate(s.captured_at)}</td>
                      <td className="px-3">{s.verified ? "✓" : "✗"}</td>
                      <td className="px-3">
                        {s.sitemap_errors > 0
                          ? `${s.sitemap_errors}E`
                          : s.sitemap_warnings > 0
                            ? `${s.sitemap_warnings}W`
                            : "ok"}
                      </td>
                      <td className="px-3">{s.indexed_pages}/{s.submitted_pages}</td>
                      <td className="px-3">{s.clicks_7d}</td>
                      <td className="px-3">{s.impressions_7d}</td>
                      <td className="px-3">{(s.ctr_7d * 100).toFixed(2)}%</td>
                      <td className="px-3">{s.position_7d > 0 ? s.position_7d.toFixed(1) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
